import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, Users, Award, BookOpen, ExternalLink, Loader2 } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { API } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AnimatedCard = ({ member, delay, size = 'md' }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  const avatarSizes = { lg: 'w-28 h-28 sm:w-32 sm:h-32', md: 'w-20 h-20 sm:w-24 sm:h-24' };
  const initials = member.name?.split(' ').filter(n => n.length > 1).map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';
  const photoSrc = member.photo_url
    ? (member.photo_url.startsWith('http') ? member.photo_url : `${BACKEND_URL}${member.photo_url}`)
    : null;

  return (
    <div ref={ref} className="flex flex-col items-center transition-all duration-700 ease-out group"
      style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transitionDelay: `${delay}ms` }}>
      <div className={`${avatarSizes[size]} rounded-full overflow-hidden shadow-lg mb-3 ring-4 ring-white`}>
        {photoSrc ? (
          <img src={photoSrc} alt={member.name} className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
        ) : null}
        <div className={`w-full h-full bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center text-white font-bold ${size === 'lg' ? 'text-2xl' : 'text-xl'}`}
          style={{ display: photoSrc ? 'none' : 'flex' }}>
          {initials}
        </div>
      </div>
      <h3 className={`font-bold text-gray-900 text-center ${size === 'lg' ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'}`} style={{ fontFamily: "'Barlow', sans-serif" }}>
        {member.name}
      </h3>
      {member.role && <p className="text-[#35AFE4] text-xs font-semibold text-center mt-0.5">{member.role}</p>}
      {member.fiche_id && (
        <Link
          to={`/annuaire/${member.fiche_id}`}
          className="inline-flex items-center gap-1 mt-1.5 px-3 py-1 text-[11px] font-semibold text-[#35AFE4] hover:text-white bg-[#eaf6fc] hover:bg-[#35AFE4] rounded-full transition-all duration-200"
          data-testid={`bureau-profile-link-${member.fiche_id}`}
        >
          <ExternalLink size={10} />
          Voir la fiche
        </Link>
      )}
    </div>
  );
};

const PublicBureauPage = () => {
  const [bureauMembers, setBureauMembers] = useState([]);
  const [councilMembers, setCouncilMembers] = useState([]);
  const [councilTitle, setCouncilTitle] = useState("Le conseil d'administration");
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/bureau/public`),
      axios.get(`${API}/site-content/bureau_intro`),
    ]).then(([bureauRes, aboutRes]) => {
      setBureauMembers(bureauRes.data.members || []);
      setCouncilMembers(bureauRes.data.council_members || []);
      setCouncilTitle(bureauRes.data.council_title || "Le conseil d'administration");
      if (aboutRes.data?.data) setAboutData(aboutRes.data.data);
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const defaultParagraphs = [
    "La SFOALC (Société Française des Ophtalmologistes Adaptateurs de Lentilles de Contact) est une société savante qui regroupe l’ensemble des ophtalmologistes qui pratiquent la contactologie médicale.",
    "Son but est de promouvoir le développement de la contactologie et de participer activement à la formation médicale continue des médecins dans le domaine des lentilles de contact, des solutions d’entretien et de tout ce qui peut s’y rapporter.",
    "Parmi ses nombreuses missions, elle organise des réunions de formation, des congrès, des ateliers pratiques de contactologie permettant aux ophtalmologistes d’accéder à une formation continue et personnalisée.",
    "Elle est également pleinement engagée dans la formation des internes afin de s’assurer que les nouvelles générations d’ophtalmologistes débuteront leur carrière en possédant toutes les bases de la prise en charge du patient porteur de lentilles de contact.",
    "Au niveau des institutions, elle joue un rôle consultatif auprès des organismes gouvernementaux (ministères), professionnels (opticiens, industrie…), de la santé (Sécurité Sociale) et de l’ophtalmologie (Académie de l’ophtalmologie…)",
    "Anciennement, Société des ophtalmologistes adaptateurs de prothèses de contact, la SFOALC est reconnue depuis de très nombreuses années pour son engagement au sein du monde médical.",
    "LA SFOALC ne cesse de se renouveler pour continuellement intégrer les nombreuses avancées technologiques de la discipline dans le respect des règles de déontologie et de bonne pratique clinique.",
    "Membre de la SFO (Société Française d’Ophtalmologie), la SFOALC possède des antennes dans chacune des régions françaises et également dans les DOM-TOM. Elle entretient des relations privilégiées avec les ophtalmologistes francophones de nombreux pays et participe notamment aux travaux de l’European Contact Lens Society of Ophthalmologists (ECLSO).",
  ];

  const paragraphs = aboutData?.paragraphs?.length > 0 ? aboutData.paragraphs : defaultParagraphs;

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-[#35AFE4]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Roboto', sans-serif" }} data-testid="bureau-page">
      <SEO title="Le Bureau - SFOALC" description="Découvrez les membres du bureau de la SFOALC et le conseil d'administration." />
      <SiteHeader />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2a6b8f 50%, #35AFE4 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-40 w-72 h-72 rounded-full bg-white/20" />
          <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full bg-white/10" />
        </div>
        <div className="max-w-5xl mx-auto px-4 pt-28 pb-16 text-center relative z-10">
          <p className="text-[#8ec8e0] text-xs font-bold uppercase tracking-[0.25em] mb-4">L'association</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="bureau-title">
            Le Bureau de la SFOALC
          </h1>
          <p className="text-white/70 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            Société Française des Ophtalmologistes Adaptateurs de Lentilles de Contact
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1000 60" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '40px' }}>
            <path fill="#ffffff" d="M0,60V50C0.9,33,126.7,0,500,0S999.1,33,1000,50V60H0Z" />
          </svg>
        </div>
      </section>

      {/* Mission cards */}
      <section className="py-12 px-4 -mt-4">
        <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-5">
          <div className="bg-[#f4fafe] rounded-xl p-6 border border-[#d0e8f0]">
            <div className="w-10 h-10 rounded-lg bg-[#35AFE4] flex items-center justify-center mb-3">
              <BookOpen size={20} className="text-white" />
            </div>
            <h3 className="font-bold text-[#1e3a5f] text-sm mb-1.5" style={{ fontFamily: "'Barlow', sans-serif" }}>Formation continue</h3>
            <p className="text-xs text-gray-500 leading-relaxed">Réunions de formation, congrès, ateliers pratiques de contactologie pour une formation personnalisée.</p>
          </div>
          <div className="bg-[#f4fafe] rounded-xl p-6 border border-[#d0e8f0]">
            <div className="w-10 h-10 rounded-lg bg-[#35AFE4] flex items-center justify-center mb-3">
              <Users size={20} className="text-white" />
            </div>
            <h3 className="font-bold text-[#1e3a5f] text-sm mb-1.5" style={{ fontFamily: "'Barlow', sans-serif" }}>Formation des internes</h3>
            <p className="text-xs text-gray-500 leading-relaxed">Engagement dans la formation des nouvelles générations d'ophtalmologistes en contactologie.</p>
          </div>
          <div className="bg-[#f4fafe] rounded-xl p-6 border border-[#d0e8f0]">
            <div className="w-10 h-10 rounded-lg bg-[#35AFE4] flex items-center justify-center mb-3">
              <Award size={20} className="text-white" />
            </div>
            <h3 className="font-bold text-[#1e3a5f] text-sm mb-1.5" style={{ fontFamily: "'Barlow', sans-serif" }}>Rôle consultatif</h3>
            <p className="text-xs text-gray-500 leading-relaxed">Conseil auprès des ministères, professionnels de santé, Sécurité Sociale et Académie d'ophtalmologie.</p>
          </div>
        </div>
      </section>

      {/* About text from DB */}
      <section className="py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-4 text-gray-600 leading-relaxed text-sm sm:text-base">
            {paragraphs.map((p, i) => (
              <div key={i} className="[&_p]:mb-4" dangerouslySetInnerHTML={{ __html: p.trim().startsWith('<') ? p : `<p>${p}</p>` }} />
            ))}
          </div>
        </div>
      </section>

      {/* Bureau members — dynamic */}
      {bureauMembers.length > 0 && (
        <section className="py-14 px-4 bg-[#f8fbfd]" data-testid="bureau-members-section">
          <div className="max-w-4xl mx-auto">
            <p className="text-[#35AFE4] text-xs font-bold uppercase tracking-[0.2em] text-center mb-2">Direction</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-12" style={{ fontFamily: "'Barlow', sans-serif" }}>
              Le Bureau
            </h2>
            <div className={`grid gap-8 sm:gap-10 ${bureauMembers.length <= 4 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5'}`}>
              {bureauMembers.map((m, i) => (
                <AnimatedCard key={m.name + i} member={m} delay={i * 150} size="lg" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Council members — dynamic */}
      {councilMembers.length > 0 && (
        <section className="py-14 px-4" data-testid="council-members-section">
          <div className="max-w-5xl mx-auto">
            <p className="text-[#35AFE4] text-xs font-bold uppercase tracking-[0.2em] text-center mb-2">Gouvernance</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-12" style={{ fontFamily: "'Barlow', sans-serif" }}>
              {councilTitle}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-8 sm:gap-10">
              {councilMembers.map((m, i) => (
                <AnimatedCard key={m.name + i} member={m} delay={i * 80} size="md" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Floating donation button */}
      <a href="https://www.helloasso.com/associations/sfoalc-société-francaise-des-ophtalmologistes-adaptateurs-de-lentilles-de-contact"
        target="_blank" rel="noopener noreferrer" className="fixed left-5 bottom-6 z-50 group" data-testid="donate-floating-btn">
        <div className="relative">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#e84393] to-[#d63031] flex items-center justify-center text-white shadow-xl hover:shadow-2xl hover:scale-110 transition-all duration-300 ring-4 ring-white">
            <Heart size={19} fill="white" stroke="white" />
          </div>
          <span className="absolute left-full ml-3 top-1/2 -translate-y-1/2 bg-white text-gray-800 rounded-full shadow-lg px-4 py-2 text-xs font-bold whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
            Soutenir la SFOALC
          </span>
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-ping opacity-75" />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full" />
        </div>
      </a>

      <Footer />
    </div>
  );
};

export default PublicBureauPage;
